import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function WhyChooseUs() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  const reasons = [
    { icon: 'ri-shield-check-line', titleKey: 'why.r1.title', descKey: 'why.r1.desc' },
    { icon: 'ri-customer-service-2-line', titleKey: 'why.r2.title', descKey: 'why.r2.desc' },
    { icon: 'ri-money-dollar-circle-line', titleKey: 'why.r3.title', descKey: 'why.r3.desc' },
    { icon: 'ri-route-line', titleKey: 'why.r4.title', descKey: 'why.r4.desc' },
    { icon: 'ri-user-star-line', titleKey: 'why.r5.title', descKey: 'why.r5.desc' },
    { icon: 'ri-heart-3-line', titleKey: 'why.r6.title', descKey: 'why.r6.desc' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-amber-500 font-semibold text-sm uppercase tracking-widest mb-3">{t('why.badge')}</p>
          <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-4">
            {t('why.title')}
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            {t('why.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {reasons.map((reason, idx) => (
            <div
              key={reason.titleKey}
              className={`group p-7 rounded-xl border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/30 transition-all duration-500 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 group-hover:bg-emerald-700 rounded-xl mb-5 transition-colors duration-300">
                <i className={`${reason.icon} text-xl text-emerald-700 group-hover:text-white transition-colors duration-300`}></i>
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-3 font-playfair">{t(reason.titleKey)}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{t(reason.descKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
